import { useMutation, useQuery } from "@tanstack/react-query";
import { useApi } from "@core/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faIdBadge,
  faCheckCircle,
  faCircleExclamation,
  faSpinner,
  faArrowUpRightFromSquare,
  faLock,
} from "@fortawesome/pro-light-svg-icons";
import { t } from "i18next";
import { useEffect, useState } from "react";

type Status = "unknown" | "verified" | "pending" | "unverified" | "revoked" | "error";

function useProviderPassStatus() {
  const api = useApi();
  return useQuery({
    queryKey: ["providerpass", "status"],
    queryFn: async (): Promise<{ status: Status; hasAccount: boolean; message?: string; licenseKey?: string; shouldAbandonProofType?: boolean }> => {
      try {
        const result = await api.GetProviderPassStatusForCurrentProvider();
        const status = (result?.Status ?? "unverified") as Status;
        return {
          status,
          hasAccount: result?.HasAccount ?? false,
          message: result?.Message,
          licenseKey: result?.LicenseKey,
          shouldAbandonProofType: result?.ShouldAbandonProofType ?? false,
        };
      } catch (e: any) {
        return { status: "error", hasAccount: false, message: e?.message };
      }
    },
    staleTime: 30_000,
  });
}

export default function ProviderPassPanel() {
  const api = useApi();
  const { data, isLoading } = useProviderPassStatus();
  const [actionError, setActionError] = useState<string | null>(null);
  const [licenseKey, setLicenseKey] = useState<string | null>(null);
  const [pendingLaunch, setPendingLaunch] = useState<{ url: string; label: string } | null>(null);
  const [lostTokenReason, setLostTokenReason] = useState("");
  const [lostTokenSuccess, setLostTokenSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (data?.licenseKey) {
      setLicenseKey(data.licenseKey);
    }
  }, [data?.licenseKey]);

  const generateLicenseKey = useMutation({
    mutationFn: () => api.GenerateProviderPassLicenseKey(),
    onSuccess: (response) => {
      if (!response?.LicenseKey) {
        setLicenseKey(null);
        setActionError(
          t(
            "providerpass.licenseKeyMissing",
            "A license key could not be generated. Please try again.",
          ),
        );
        return;
      }

      setActionError(null);
      setLicenseKey(response.LicenseKey);
    },
    onError: (error: Error) => {
      setActionError(
        error?.message
          ?? t(
            "providerpass.licenseKeyFailed",
            "License key generation failed. Please try again.",
          ),
      );
    },
  });

  const launchProofing = useMutation({
    mutationFn: () => api.LaunchProviderPassProofing(),
    onSuccess: (response) => {
      if (!response?.LaunchUrl) {
        setActionError(t("providerpass.launchMissingUrl", "Identity verification could not be started."));
        return;
      }
      setActionError(null);
      setPendingLaunch({ url: response.LaunchUrl, label: t("providerpass.continueVerify", "Continue to identity verification") });
    },
    onError: (error: Error) => {
      setActionError(
        error?.message
          ?? t("providerpass.launchFailed", "Identity verification could not be started. Please try again."),
      );
    },
  });

  const launchSelfAdmin = useMutation({
    mutationFn: () => api.LaunchProviderPassSelfAdmin(),
    onSuccess: (response) => {
      if (!response?.LaunchUrl) {
        setActionError(t("providerpass.selfAdminMissingUrl", "Credential management could not be started."));
        return;
      }
      setActionError(null);
      setPendingLaunch({ url: response.LaunchUrl, label: t("providerpass.continueManage", "Continue to credential management") });
    },
    onError: (error: Error) => {
      setActionError(
        error?.message
          ?? t("providerpass.selfAdminFailed", "Credential management could not be started. Please try again."),
      );
    },
  });

  const launchWebcamProofing = useMutation({
    mutationFn: () => api.LaunchProviderPassWebcamProofing(),
    onSuccess: (response) => {
      if (!response?.LaunchUrl) {
        setActionError(t("providerpass.webcamMissingUrl", "Webcam verification could not be started."));
        return;
      }
      setActionError(null);
      setPendingLaunch({ url: response.LaunchUrl, label: t("providerpass.continueWebcam", "Continue to webcam scheduling") });
    },
    onError: (error: Error) => {
      setActionError(
        error?.message
          ?? t("providerpass.webcamFailed", "Webcam verification could not be started. Please try again."),
      );
    },
  });

  const reportLostToken = useMutation({
    mutationFn: async () => {
      return await api.api.post("/pluto/providerpass/report-lost-token", {
        Reason: lostTokenReason,
      });
    },
    onSuccess: () => {
      setActionError(null);
      setPendingLaunch(null);
      setLostTokenSuccess(
        t(
          "providerpass.lostTokenReported",
          "Lost-token report submitted. EPCS access has been locked immediately.",
        ),
      );
      setLostTokenReason("");
      window.location.reload();
    },
    onError: (error: any) => {
      const backendMessages = error?.response?.data?.messages;
      if (Array.isArray(backendMessages) && backendMessages.length > 0) {
        setActionError(backendMessages.join(" "));
        return;
      }

      setActionError(
        error?.message
          ?? t(
            "providerpass.lostTokenFailed",
            "Lost-token report failed. Please try again.",
          ),
      );
    },
  });

  // Note: we deliberately do NOT auto-open a window here. Opening a blank window and later
  // redirecting it (the common workaround for popup blockers) is unreliable — Brave and other
  // browsers/extensions specifically detect and block that "open blank then redirect" pattern
  // since it's also used by malicious ad popups. Instead we fetch the URL first (inline
  // spinner, no window), then require one more explicit click to open it — that click is a
  // fresh, direct user gesture, so window.open() is never blocked.
  const requestLicenseKey = () => {
    setActionError(null);
    generateLicenseKey.mutate();
  };

  const startProofing = () => {
    setActionError(null);
    setLostTokenSuccess(null);
    setPendingLaunch(null);
    launchProofing.mutate();
  };

  const openSelfAdmin = () => {
    setActionError(null);
    setLostTokenSuccess(null);
    setPendingLaunch(null);
    launchSelfAdmin.mutate();
  };

  const startWebcamProofing = () => {
    setActionError(null);
    setLostTokenSuccess(null);
    setPendingLaunch(null);
    launchWebcamProofing.mutate();
  };

  const submitLostToken = () => {
    setActionError(null);
    setLostTokenSuccess(null);
    reportLostToken.mutate();
  };

  const openPendingLaunch = () => {
    if (!pendingLaunch) return;
    window.open(pendingLaunch.url, "_blank", "noopener,noreferrer");
    setPendingLaunch(null);
  };

  const status = data?.status ?? "unknown";
  const hasAccount = data?.hasAccount ?? false;
  const needsWebcamFallback = data?.shouldAbandonProofType === true;
  const requiresLicenseKey = status === "unverified";
  const hasLicenseKey = !requiresLicenseKey || Boolean(licenseKey);
  const isBusy =
    generateLicenseKey.isPending
    || launchProofing.isPending
    || launchSelfAdmin.isPending
    || launchWebcamProofing.isPending
    || reportLostToken.isPending;

  return (
    <div className="acct-section" data-testid="providerpass-panel" data-providerpass-status={status}>
      <h2 className="acct-section-title">
        <FontAwesomeIcon icon={faIdBadge} style={{ marginRight: 10 }} />
        {t("providerpass.title", "DEA EPCS Identity")}
      </h2>

      <div className="acct-providerpass-status">
        {(isLoading || isBusy) && <FontAwesomeIcon icon={faSpinner} spin className="status-warning" />}
        {status === "verified" && !isBusy && (
          <>
            <FontAwesomeIcon icon={faCheckCircle} className="status-verified" />
            <span>{t("providerpass.status.verified", "Verified")}</span>
          </>
        )}
        {status === "unverified" && !isBusy && (
          <>
            <FontAwesomeIcon icon={faCircleExclamation} className="status-warning" />
            <span>{t("providerpass.status.unverified", "Not verified")}</span>
          </>
        )}
        {status === "pending" && !isBusy && (
          <>
            <FontAwesomeIcon icon={faSpinner} spin className="status-warning" />
            <span>{t("providerpass.status.pending", "Verification in progress")}</span>
          </>
        )}
        {status === "revoked" && !isBusy && (
          <>
            <FontAwesomeIcon icon={faCircleExclamation} className="status-error" />
            <span>{t("providerpass.status.revoked", "Revoked by administrator")}</span>
          </>
        )}
        {status === "error" && !isBusy && (
          <>
            <FontAwesomeIcon icon={faCircleExclamation} className="status-error" />
            <span>{data?.message ?? t("providerpass.status.error", "Status unavailable")}</span>
          </>
        )}
      </div>

      <p className="acct-body-text">
        {status === "revoked"
          ? t(
              "providerpass.revokedDescription",
              "Your identity verification was revoked by an administrator. You will not be able to sign prescriptions for controlled substances until this is resolved.",
            )
          : needsWebcamFallback
            ? t(
                "providerpass.webcamDescription",
                "We couldn't verify your identity automatically. Please schedule a brief webcam consultation to complete verification.",
              )
            : t(
                "providerpass.description",
                "Prescribers must complete federal identity verification before signing prescriptions for controlled substances.",
              )}
      </p>

      {licenseKey && (
        <div className="acct-providerpass-license-key" data-testid="providerpass-license-key">
          <p className="acct-providerpass-license-notice">
            {t(
              "providerpass.licenseKeyCopyNotice",
              "Please copy this value before proceeding to the proofing step.",
            )}
          </p>
          <p className="acct-body-text" style={{ marginBottom: 8 }}>
            {t("providerpass.licenseKeyLabel", "Your license key")}
          </p>
          <code className="acct-providerpass-license-value">
            {licenseKey}
          </code>
        </div>
      )}

      {actionError && (
        <div className="acct-error visible" role="alert">
          {actionError}
        </div>
      )}

      {lostTokenSuccess && (
        <div className="acct-success visible" role="status">
          {lostTokenSuccess}
        </div>
      )}

      <div style={{ marginTop: 16, borderTop: "1px solid #ececec", paddingTop: 14 }}>
        {hasAccount && (
          <>
            <p className="acct-body-text" style={{ marginBottom: 8 }}>
              {t(
                "providerpass.lostTokenTitle",
                "Lost, stolen, or compromised token? Report it now to lock controlled-signing access.",
              )}
            </p>
            <textarea
              className="acct-textarea"
              rows={3}
              value={lostTokenReason}
              onChange={(e) => setLostTokenReason(e.target.value)}
              placeholder={t("providerpass.lostTokenPlaceholder", "Describe what happened")}
              disabled={isBusy}
            />
            <div className="acct-actions" style={{ justifyContent: "flex-start", marginTop: 10, marginBottom: 0 }}>
              <button
                type="button"
                className="acct-secondary-btn"
                onClick={submitLostToken}
                disabled={isBusy || lostTokenReason.trim().length < 5}
              >
                {reportLostToken.isPending
                  ? t("providerpass.reporting", "Reporting...")
                  : t("providerpass.reportLostToken", "Report lost token and lock EPCS")}
                <FontAwesomeIcon icon={faLock} style={{ marginLeft: 8 }} />
              </button>
            </div>
          </>
        )}
      </div>

      <div className="acct-actions" style={{ justifyContent: "flex-start", marginBottom: 0, gap: 12 }}>
        {pendingLaunch ? (
          <button
            type="button"
            onClick={openPendingLaunch}
            className="acct-save-btn"
          >
            {pendingLaunch.label} <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ marginLeft: 6 }} />
          </button>
        ) : status === "revoked" ? (
          <a href="/contact" className="acct-save-btn">
            {t("providerpass.contactUs", "Contact us")}
          </a>
        ) : (
          <>
            {status !== "verified" && (
              needsWebcamFallback ? (
                <>
                  <button
                    type="button"
                    onClick={startWebcamProofing}
                    disabled={isBusy}
                    className="acct-save-btn"
                  >
                    {launchWebcamProofing.isPending
                      ? t("providerpass.launchingWebcam", "Starting webcam scheduling...")
                      : t("providerpass.scheduleWebcam", "Schedule webcam consultation")}
                  </button>
                  <button
                    type="button"
                    onClick={startWebcamProofing}
                    disabled={isBusy}
                    className="acct-secondary-btn"
                  >
                    {launchWebcamProofing.isPending
                      ? t("providerpass.launching", "Starting verification...")
                      : t("providerpass.haveConfirmationCode", "Have a confirmation code?")}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={startProofing}
                  disabled={isBusy}
                  className="acct-save-btn"
                >
                  {launchProofing.isPending
                    ? t("providerpass.launching", "Starting verification...")
                    : generateLicenseKey.isPending
                      ? t("providerpass.generatingLicenseKey", "Preparing license...")
                      : t("providerpass.verify", "Verify identity")}
                </button>
              )
            )}
            {hasAccount && (
              <button
                type="button"
                onClick={openSelfAdmin}
                disabled={isBusy || status === "unverified"}
                className="acct-secondary-btn"
              >
                {launchSelfAdmin.isPending
                  ? t("providerpass.openingSelfAdmin", "Opening...")
                  : t("providerpass.manage", "Manage credentials")}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
